/**
 * EDR — Sensor installer hooks (tag-based pre-baked enrollment).
 *
 * Backs Settings → Sensor installers. All queries tolerate a 404 from
 * the backend (endpoint not shipped yet) by returning an
 * "unavailable" sentinel so the UI can show an empty state instead of
 * a hard error. Mirrors the pattern used by `useEdrEnrollment`.
 *
 * Spec: docs/backend-requirements/edr-sensor-installer-api.md
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { edrGet, edrPost, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  BuildInstallerInput,
  InstallerBuild,
  InstallerSummary,
  InstallerTag,
} from "@/types/edr-installers";

const BASE = "/api/v1/connectors/edr/installers";
const LIST_KEY = ["edr", "installers"] as const;
const TAGS_KEY = ["edr", "installers", "tags"] as const;

const STALE_MS = 30_000;
const POLL_MS = 30_000;

export const INSTALLERS_UNAVAILABLE = Symbol("installers-unavailable");

export type InstallerListResult =
  | { kind: "ok"; rows: InstallerSummary[] }
  | { kind: "unavailable" };

export type InstallerTagListResult =
  | { kind: "ok"; tags: InstallerTag[] }
  | { kind: "unavailable" };

export function useInstallerTags() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: TAGS_KEY,
    staleTime: STALE_MS,
    queryFn: async (): Promise<InstallerTagListResult> => {
      try {
        const data = await edrGet<{ tags?: InstallerTag[] } | InstallerTag[]>(
          `${BASE}/tags`,
        );
        const tags = Array.isArray(data) ? data : data?.tags ?? [];
        return { kind: "ok", tags };
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) {
          return { kind: "unavailable" };
        }
        throw err;
      }
    },
  });
}

export function useInstallers() {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    enabled,
    queryKey: LIST_KEY,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    queryFn: async (): Promise<InstallerListResult> => {
      try {
        const data = await edrGet<
          { installers?: InstallerSummary[] } | InstallerSummary[]
        >(BASE);
        const rows = Array.isArray(data) ? data : data?.installers ?? [];
        return { kind: "ok", rows };
      } catch (err) {
        if (err instanceof EdrApiError && err.status === 404) {
          return { kind: "unavailable" };
        }
        throw err;
      }
    },
  });
}

export function useBuildInstaller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BuildInstallerInput): Promise<InstallerBuild> => {
      return await edrPost<InstallerBuild>(BASE, {
        tagId: input.tagId,
        platform: input.platform,
        channel: input.channel ?? "stable",
        expiresInDays: input.expiresInDays,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Installer API not yet available", {
          description: "The sensor-installer endpoint hasn't shipped yet.",
        });
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to build installer";
      toast.error(message);
    },
  });
}

export function useRefreshInstallerUrl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      id: string,
    ): Promise<{ downloadUrl: string; downloadUrlExpiresAt: string }> => {
      return await edrPost(
        `${BASE}/${encodeURIComponent(id)}/refresh-url`,
        {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Installer API not yet available", {
          description: "The sensor-installer endpoint hasn't shipped yet.",
        });
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to refresh download URL";
      toast.error(message);
    },
  });
}

export function useRevokeInstaller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await edrPost(`${BASE}/${encodeURIComponent(id)}/revoke`, {});
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      toast.success("Installer revoked");
    },
    onError: (err) => {
      if (err instanceof EdrApiError && err.status === 404) {
        toast.message("Installer API not yet available", {
          description: "The sensor-installer endpoint hasn't shipped yet.",
        });
        return;
      }
      const message =
        err instanceof Error ? err.message : "Failed to revoke installer";
      toast.error(message);
    },
  });
}
